import {
   CADENCE_PRESETS,
   KIND_PRESET,
} from '@forwardflow/features/campaign-flow/data/mock'
import {
   IPlaybook,
   ITouch,
   TCadencePreset,
   TCampaignKind,
   TLeadType,
   TPlaybookContent,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { plural } from '@forwardflow/features/campaign-flow/utils/text'

/** A touch as the lead experiences it, with its words. Voicemails follow their call. */
export interface ITimelineItem {
   key: string
   kind: 'call' | 'voicemail' | 'text'
   label: string
   when: string
   day: number
   time: string
   /** Index into script (call), texts, or voicemails. */
   index: number
}

export const sortTouches = (touches: ITouch[]) =>
   [...touches].sort((a, b) => a.day - b.day || a.time.localeCompare(b.time))

export const timeline = (
   pb: Pick<IPlaybook, 'touches' | 'texts' | 'voicemails'>
): ITimelineItem[] => {
   const out: ITimelineItem[] = []
   let calls = 0
   let texts = 0
   let vms = 0
   for (const t of sortTouches(pb.touches)) {
      if (t.action === 'call') {
         calls += 1
         out.push({
            key: t.id,
            kind: 'call',
            label: `Call ${calls}`,
            when: `Day ${t.day}`,
            day: t.day,
            time: t.time,
            index: calls - 1,
         })
         if (t.voicemail) {
            vms += 1
            out.push({
               key: `${t.id}-vm`,
               kind: 'voicemail',
               label: `Voicemail ${vms}`,
               when: 'if no answer',
               day: t.day,
               time: t.time,
               index: vms - 1,
            })
         }
      } else {
         texts += 1
         out.push({
            key: t.id,
            kind: 'text',
            label: `Text ${texts}`,
            when: `Day ${t.day}`,
            day: t.day,
            time: t.time,
            index: texts - 1,
         })
      }
   }
   return out
}

export const counts = (touches: ITouch[]) => ({
   calls: touches.filter((t) => t.action === 'call').length,
   texts: touches.filter((t) => t.action === 'text').length,
   voicemails: touches.filter((t) => t.action === 'call' && t.voicemail).length,
   days: touches.length ? Math.max(...touches.map((t) => t.day)) : 0,
})

export const cadenceSummary = (pb: Pick<IPlaybook, 'touches' | 'preset'>) => {
   const c = counts(pb.touches)
   const name =
      pb.preset === 'custom' ? 'Your timing' : CADENCE_PRESETS[pb.preset].label
   return `${name} · ${plural(pb.touches.length, 'touch', 'touches')} over ${plural(c.days, 'day')}`
}

export const contentSummary = (pb: IPlaybook) => {
   const c = counts(pb.touches)
   return `${pb.origin === 'mine' ? 'Your version of the buyer' : 'Buyer'} outbound call script · ${plural(c.texts, 'text')} · ${plural(c.voicemails, 'voicemail')}`
}

/**
 * Keeps texts and voicemails in step with the cadence: one text per text touch,
 * one voicemail per call that leaves one. New items start from a sensible line.
 */
/**
 * The words stay the library's: texts and voicemails are never padded or
 * cut to the timing. The engine sends them in order and repeats the last
 * one when the timing has more touches (`exhaustion: repeat-last`). Only a
 * timing that needs a text or a voicemail where there is none gets one.
 */
export const syncMessages = (pb: IPlaybook): IPlaybook => {
   const c = counts(pb.touches)
   const sync = <T extends Pick<IPlaybook, 'texts' | 'voicemails'>>(
      x: T
   ): T => ({
      ...x,
      texts:
         c.texts > 0 && !x.texts.length
            ? [
                 {
                    id: `s${Date.now().toString(36)}`,
                    text: 'Hi {first name}, {assistant name} again from {organization}. Happy to help whenever you are ready.',
                 },
              ]
            : x.texts,
      voicemails:
         c.voicemails > 0 && !x.voicemails.length
            ? [
                 {
                    id: `v${Date.now().toString(36)}`,
                    text: "Hi {first name}, it's {assistant name} with {organization}. I'll send you a text. Talk soon.",
                 },
              ]
            : x.voicemails,
   })
   const byType = pb.byType
      ? (Object.fromEntries(
           Object.entries(pb.byType).map(([t, x]) => [t, x && sync(x)])
        ) as IPlaybook['byType'])
      : undefined
   return { ...sync(pb), ...(byType ? { byType } : {}) }
}

/**
 * What the rehearsal says on the call: the opening line. The sections are
 * instructions the caller follows, not lines it reads out, so a script with
 * no opening line gets a short introduction instead.
 */
export const spokenLines = (pb: IPlaybook): string[] => [
   pb.opening?.trim()
      ? pb.opening
      : 'Hi {first name}, this is {assistant name}. Do you have a quick minute?',
]

/** The text the k-th text touch sends: in order, the last one again once they run out. */
export const textFor = (pb: Pick<IPlaybook, 'texts'>, k: number) =>
   pb.texts[Math.min(k, pb.texts.length - 1)]
export const voicemailFor = (pb: Pick<IPlaybook, 'voicemails'>, k: number) =>
   pb.voicemails[Math.min(k, pb.voicemails.length - 1)]

export const applyPreset = (
   pb: IPlaybook,
   preset: Exclude<TCadencePreset, 'custom'>
): IPlaybook =>
   syncMessages({
      ...pb,
      preset,
      touches: CADENCE_PRESETS[preset].touches.map((t) => ({ ...t })),
   })

/** Next free day after the last touch, for "Add a call" / "Add a text". */
export const nextDay = (touches: ITouch[]) =>
   touches.length ? Math.max(...touches.map((t) => t.day)) + 1 : 1

/* ---------- Words per lead type ---------- */

const CONTENT_KEYS = [
   'origin',
   'scriptName',
   'messageGroupName',
   'scriptId',
   'messageGroupId',
   'opening',
   'script',
   'texts',
   'voicemails',
   'replyInstructions',
] as const

export const pickContent = (pb: TPlaybookContent): TPlaybookContent =>
   Object.fromEntries(
      CONTENT_KEYS.map((k) => [k, pb[k]])
   ) as unknown as TPlaybookContent

/** The words for one lead type; the top-level words when it has none of its own. */
export const contentOf = (pb: IPlaybook, type: TLeadType): TPlaybookContent =>
   pb.byType?.[type] ?? pickContent(pb)

/** The playbook as one lead type sees it: shared timing, that type's words. */
export const viewFor = (pb: IPlaybook, type: TLeadType): IPlaybook => ({
   ...pb,
   ...contentOf(pb, type),
})

/**
 * Store an edited view back: timing is shared, words go to that lead type.
 * The first lead type's words are mirrored at the top level.
 */
export const writeFor = (
   pb: IPlaybook,
   type: TLeadType,
   next: IPlaybook,
   types: TLeadType[]
): IPlaybook => {
   const content = pickContent(next)
   const out: IPlaybook = {
      ...pb,
      preset: next.preset,
      touches: next.touches,
      usedByTeams: next.usedByTeams,
      byType: { ...pb.byType, [type]: content },
      ...(type === (types[0] ?? type) ? content : {}),
   }
   return syncMessages(out)
}

/**
 * The timing a kind of lead starts with: fast for new leads, spaced out for
 * cold ones, short for activity. Only applied while the timing is still a
 * preset the user has not changed.
 */
export const startTiming = (
   pb: IPlaybook,
   kind: TCampaignKind | undefined
): IPlaybook =>
   !kind || pb.preset === 'custom' || pb.preset === KIND_PRESET[kind]
      ? pb
      : applyPreset(pb, KIND_PRESET[kind])

/** Every chosen lead type has its own words; the first one is mirrored at the top. */
export const withTypes = (pb: IPlaybook, types: TLeadType[]): IPlaybook => {
   if (!types.length) return pb
   const byType = { ...pb.byType }
   types.forEach((t) => {
      if (!byType[t]) byType[t] = JSON.parse(JSON.stringify(pickContent(pb)))
   })
   return syncMessages({
      ...pb,
      byType,
      ...(byType[types[0]] as TPlaybookContent),
   })
}

/** Lead types whose words differ between two versions of the playbook. */
export const changedTypes = (
   before: IPlaybook,
   now: IPlaybook,
   types: TLeadType[]
) =>
   types.filter(
      (t) =>
         JSON.stringify(contentOf(before, t)) !==
         JSON.stringify(contentOf(now, t))
   )

/** Mark these lead types' words as the user's own version. */
export const markMine = (
   pb: IPlaybook,
   types: TLeadType[],
   all: TLeadType[]
): IPlaybook =>
   types.reduce(
      (acc, t) => writeFor(acc, t, { ...viewFor(acc, t), origin: 'mine' }, all),
      pb
   )
